import { useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { useGetInsuranceCarriersQuery } from '@/api/hooks/queries/use-get-insurance-carriers-query';
import aetnaLogo from '@/assets/insurance-logos/aetna.svg';
import anthemLogo from '@/assets/insurance-logos/anthem.svg';
import fidelisCareLogo from '@/assets/insurance-logos/fidelis-care.svg';
import healthfirstLogo from '@/assets/insurance-logos/healthfirst.svg';
import humanaLogo from '@/assets/insurance-logos/humana.svg';
import metroplusLogo from '@/assets/insurance-logos/metroplus.svg';
import sunshineHealthLogo from '@/assets/insurance-logos/sunshine-health.svg';
import unitedHealthcareLogo from '@/assets/insurance-logos/united-healthcare.svg';
import { GeneralErrorIndicator } from '@/components/GeneralErrorIndicator.tsx';
import { Button } from '@/components/ui/button.tsx';
import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox.tsx';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field.tsx';
import { Input } from '@/components/ui/input.tsx';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group.tsx';
import { cn } from '@/lib/utils';
import type { stateNames } from '@/lib/zipcode';
import {
  CARRIER_TYPE,
  type InsuranceCarrier,
  PLAN_FAMILY,
  type PlanFamily,
  type QueuedInsurance,
  type UnprovidedInsurance,
  type UnverifiedInsurance,
  groupByType,
} from '@/types/insurance.type.ts';
import {
  FoundCoverageBanner,
  getFoundBaseCoverageLabel,
} from '@/views/insurance/flows/eligibility-revamp/components/FoundCoverageBanner.tsx';
import { ProgressiveQuestionReveal } from '@/views/insurance/flows/eligibility-revamp/components/ProgressiveQuestionReveal.tsx';

const selectionSchema = z.enum(['base', 'managed']);

type Selection = z.infer<typeof selectionSchema>;

interface AskManagedFormProps {
  family: PlanFamily;
  state: keyof typeof stateNames | undefined;
  hasDiscoveredBaseInsurance: boolean;
  showDiscoveredBaseContext: boolean;
  showError?: boolean;
  initialInsurance?: UnverifiedInsurance;
  onConfirmBase: () => void;
  onSubmit: (insurance: QueuedInsurance) => void;
  onCannotProvide: (insurance: UnprovidedInsurance) => void;
}

const formId = 'ask-managed-form';
const hiddenManagedPlanNames = new Set(['Medicaid', 'Medicare']);

type Logo = {
  src: string;
  alt: string;
  className?: string;
};

type ManagedPlanContent = {
  title: {
    default: string;
    withDiscoveredBase: string;
  };
  helperCopy: string;
  noLabel: string;
  logos: Logo[];
};

const medicareContent: ManagedPlanContent = {
  title: {
    default: 'Do you have Medicare Advantage (Part C)?',
    withDiscoveredBase: 'Do you also have Medicare Advantage (Part C)?',
  },
  helperCopy: "If your card shows a private insurer's name, you have Advantage.",
  noLabel: 'No, just Original Medicare',
  logos: [
    { src: unitedHealthcareLogo, alt: 'UnitedHealthcare' },
    { src: aetnaLogo, alt: 'Aetna' },
    { src: humanaLogo, alt: 'Humana' },
    { src: anthemLogo, alt: 'Anthem Blue Cross Blue Shield' },
  ],
};

const newYorkMedicaidContent: ManagedPlanContent = {
  title: {
    default: 'Do you have a managed Medicaid plan?',
    withDiscoveredBase: 'Do you also have a managed Medicaid plan?',
  },
  helperCopy: 'If your card shows a private insurer, you have a managed plan.',
  noLabel: 'No, just New York Medicaid',
  logos: [
    { src: fidelisCareLogo, alt: 'Fidelis Care', className: 'max-h-6' },
    { src: healthfirstLogo, alt: 'Healthfirst' },
    { src: metroplusLogo, alt: 'MetroPlusHealth' },
    { src: unitedHealthcareLogo, alt: 'UnitedHealthcare' },
  ],
};

const floridaMedicaidContent: ManagedPlanContent = {
  title: {
    default: 'Do you have a managed Medicaid plan?',
    withDiscoveredBase: 'Do you also have a managed Medicaid plan?',
  },
  helperCopy: 'If your card shows a private insurer, you have a managed plan.',
  noLabel: 'No, just my Gold Card',
  logos: [
    { src: sunshineHealthLogo, alt: 'Sunshine Health', className: 'max-h-6' },
    { src: unitedHealthcareLogo, alt: 'UnitedHealthcare' },
    { src: humanaLogo, alt: 'Humana' },
    { src: aetnaLogo, alt: 'Aetna' },
  ],
};

const formSchema = z.object({
  selection: selectionSchema.optional(),
  internalId: z.string().optional(),
  memberId: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

export function AskManagedForm({
  family,
  state,
  hasDiscoveredBaseInsurance,
  showDiscoveredBaseContext,
  showError,
  initialInsurance,
  onConfirmBase,
  onSubmit,
  onCannotProvide,
}: AskManagedFormProps) {
  const isMedicare = family === PLAN_FAMILY.MEDICARE;
  const baseType = isMedicare ? CARRIER_TYPE.MEDICARE : CARRIER_TYPE.MEDICAID;
  const managedType = isMedicare
    ? CARRIER_TYPE.MEDICARE_ADVANTAGE
    : CARRIER_TYPE.MEDICAID_MANAGED_CARE;

  let initialSelection: Selection | undefined;
  if (initialInsurance?.carrier.carrierType === baseType) {
    initialSelection = 'base';
  } else if (initialInsurance?.carrier.carrierType === managedType) {
    initialSelection = 'managed';
  }

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      selection: initialSelection,
      memberId: initialInsurance?.memberId ?? '',
      internalId: initialInsurance?.carrier.internalId ?? '',
    },
  });
  const selection = useWatch({ control: form.control, name: 'selection' });

  const {
    data: carriers = [],
    isLoading,
    isSuccess,
    isError,
  } = useGetInsuranceCarriersQuery(state);

  const carriersByType = useMemo(() => groupByType(carriers), [carriers]);
  const baseCarriers = useMemo(
    () => carriersByType.get(baseType) ?? [],
    [carriersByType, baseType]
  );
  const managedPlanCarriers = useMemo(
    () =>
      (carriersByType.get(managedType) ?? [])
        .filter((carrier) => !hiddenManagedPlanNames.has(carrier.name))
        .sort((a, b) => Number(a.isOther) - Number(b.isOther)),
    [carriersByType, managedType]
  );
  const baseCarrierName = isMedicare ? 'Medicare' : 'Medicaid';
  const baseCarrier = useMemo(
    () => baseCarriers.find((c) => c.name === baseCarrierName) ?? null,
    [baseCarriers, baseCarrierName]
  );

  const getSelectedManagedPlanCarrier = (internalId: string) =>
    managedPlanCarriers.find((c) => c.internalId === internalId);

  const validateAndSubmit = () => {
    if (!selection) {
      if (isMedicare) {
        form.setError('selection', {
          message: 'Please select whether you have Medicare Advantage.',
        });
      } else {
        form.setError('selection', {
          message: 'Please select whether your Medicaid plan is managed.',
        });
      }
      return;
    }

    if (selection === 'base') {
      if (hasDiscoveredBaseInsurance) {
        onConfirmBase();
        return;
      }

      const data = form.getValues();

      if (!data.memberId) {
        form.setError('memberId', { message: 'Member ID is required' });
        return;
      }

      if (!baseCarrier) {
        throw new Error(`Base carrier "${baseCarrierName}" not found in carrier list`);
      }

      onSubmit({ carrier: baseCarrier, memberId: data.memberId });
      return;
    }

    if (selection === 'managed') {
      const data = form.getValues();
      let hasError = false;

      if (!data.internalId) {
        form.setError('internalId', { message: 'Please select a plan' });
        hasError = true;
      }
      if (!data.memberId) {
        form.setError('memberId', { message: 'Member ID is required' });
        hasError = true;
      }
      if (hasError) return;

      const carrier = getSelectedManagedPlanCarrier(data.internalId!);
      if (!carrier) throw new Error("Selected carrier that doesn't exist.");
      onSubmit({ carrier, memberId: data.memberId! });
    }
  };

  const handleCannotProvide = () => {
    const data = form.getValues();
    onCannotProvide({
      carrierType: selection === 'base' ? baseType : managedType,
      carrier:
        selection === 'base'
          ? (baseCarrier ?? undefined)
          : getSelectedManagedPlanCarrier(data.internalId ?? ''),
      memberId: data.memberId ?? '',
    });
  };

  let content = medicareContent;

  if (!isMedicare && state === 'FL') {
    content = floridaMedicaidContent;
  } else if (!isMedicare) {
    content = newYorkMedicaidContent;
  }

  const baseMemberIdLabel = isMedicare ? 'Medicare ID' : 'Medicaid ID';
  const cannotProvideBaseText = isMedicare
    ? "I don't know my Medicare ID"
    : "I don't know my Medicaid ID";
  const showMemberIdOnly = selection === 'base' && !hasDiscoveredBaseInsurance;
  const showCarrierAndMemberId = selection === 'managed';
  const showInputFields = showMemberIdOnly || showCarrierAndMemberId;
  const plansNotReady = !isSuccess || !state;
  const title = showDiscoveredBaseContext
    ? content.title.withDiscoveredBase
    : content.title.default;
  const foundCoverageLabel = getFoundBaseCoverageLabel(family, state);

  return (
    <>
      <CardHeader>
        {hasDiscoveredBaseInsurance && (
          <FoundCoverageBanner>{foundCoverageLabel}</FoundCoverageBanner>
        )}
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <ManagedPlanHelper copy={content.helperCopy} logos={content.logos} />

        {showError && (
          <div className="rounded-md bg-rose-50 p-4 text-sm text-rose-700">
            We couldn't verify your insurance. Please check your information and try again.
          </div>
        )}

        <form
          className="space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            validateAndSubmit();
          }}
          id={formId}
        >
          <FieldGroup className="space-y-2">
            <FieldSet>
              <FieldLegend className="sr-only">
                {isMedicare ? 'Do you have Medicare Advantage?' : 'Is your Medicaid managed?'}
              </FieldLegend>
              <Controller
                name="selection"
                control={form.control}
                render={({ field, fieldState }) => (
                  <>
                    <RadioGroup
                      value={field.value ?? ''}
                      onValueChange={(value) => {
                        field.onChange(selectionSchema.parse(value));
                        form.clearErrors(field.name);
                      }}
                      className="flex flex-col gap-4"
                    >
                      <Field orientation="horizontal" className="flex items-center gap-2">
                        <RadioGroupItem value="managed" id={`${formId}-managed`} />
                        <FieldLabel htmlFor={`${formId}-managed`}>
                          Yes, from a private insurer
                        </FieldLabel>
                      </Field>

                      <Field orientation="horizontal" className="flex items-center gap-2">
                        <RadioGroupItem value="base" id={`${formId}-base`} />
                        <FieldLabel htmlFor={`${formId}-base`}>{content.noLabel}</FieldLabel>
                      </Field>
                    </RadioGroup>
                    <FieldError errors={[fieldState.error]} />
                  </>
                )}
              />
            </FieldSet>

            {showCarrierAndMemberId && (
              <Controller
                name="internalId"
                control={form.control}
                render={({ field, fieldState }) => {
                  const selectedCarrier =
                    managedPlanCarriers.find((c) => c.internalId === field.value) ?? null;

                  return (
                    <ProgressiveQuestionReveal>
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor={`${formId}-internalId`}>
                          {isMedicare ? 'Medicare Advantage Carrier' : 'Managed Medicaid Plan'}
                        </FieldLabel>
                        <Combobox
                          items={managedPlanCarriers}
                          itemToStringLabel={(carrier) =>
                            carrier.isOther ? carrier.name : carrier.displayName
                          }
                          value={selectedCarrier}
                          onValueChange={(carrier) => field.onChange(carrier?.internalId ?? '')}
                          disabled={plansNotReady}
                        >
                          <ComboboxInput
                            placeholder={isLoading ? 'Loading...' : 'Select a carrier...'}
                            id={`${formId}-internalId`}
                            aria-label="Plan name"
                            aria-invalid={fieldState.invalid}
                            aria-busy={isLoading}
                            disabled={plansNotReady}
                          />
                          <ComboboxContent>
                            <ComboboxEmpty>No carriers found matching that keyword</ComboboxEmpty>
                            <ComboboxList>
                              {(carrier: InsuranceCarrier) => (
                                <ComboboxItem key={carrier.internalId} value={carrier}>
                                  {carrier.name}
                                </ComboboxItem>
                              )}
                            </ComboboxList>
                          </ComboboxContent>
                        </Combobox>
                        {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                      </Field>
                    </ProgressiveQuestionReveal>
                  );
                }}
              />
            )}

            {showInputFields && (
              <Controller
                name="memberId"
                control={form.control}
                render={({ field, fieldState }) => (
                  <ProgressiveQuestionReveal>
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={`${formId}-memberId`}>
                        {selection === 'base' ? baseMemberIdLabel : 'Member ID'}
                      </FieldLabel>
                      <Input
                        {...field}
                        placeholder="(e.g., AA11111A)"
                        id={`${formId}-memberId`}
                        aria-invalid={fieldState.invalid}
                        autoComplete="off"
                      />
                      {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                    </Field>
                  </ProgressiveQuestionReveal>
                )}
              />
            )}
          </FieldGroup>

          {isError && (
            <GeneralErrorIndicator>
              Failed to load insurance carriers. Please try again.
            </GeneralErrorIndicator>
          )}

          <div className="mt-8 w-full space-y-8 justify-self-center sm:w-auto sm:space-y-4.5 sm:justify-self-start">
            <Button className="w-full sm:w-auto sm:min-w-[186px]" type="submit">
              Continue
            </Button>

            {showInputFields && (
              <Button
                className="justify-self-center sm:justify-self-start sm:px-0"
                onClick={handleCannotProvide}
                type="button"
                size="sm"
                variant="link"
              >
                {selection === 'base' ? cannotProvideBaseText : "I don't have my plan info"}
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </>
  );
}

function ManagedPlanHelper({ copy, logos }: { copy: string; logos: Logo[] }) {
  return (
    <CardDescription className="mt-0 flex flex-col items-start gap-2 text-main">
      <p className="text-sm leading-[1.15]">{copy}</p>
      <div className="flex flex-col gap-2">
        <p className="text-xs leading-[1.4] font-medium">Common insurances include:</p>
        <div className="grid h-[26px] w-[282px] max-w-full grid-cols-4 items-center gap-2">
          {logos.map((logo) => (
            <img
              key={logo.alt}
              src={logo.src}
              alt={logo.alt}
              className={cn('h-full w-full object-contain', logo.className)}
            />
          ))}
        </div>
      </div>
    </CardDescription>
  );
}
